import "./timelineView.scss";

import { List } from "immutable";
import { clone, each, isEqual, isNil, omit, throttle } from "lodash";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { createRef, PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";

import { withContainerContext } from "../../components/container/ContainerContext";
import { asDropTarget } from "../../components/dragAndDrop/DroppableComponent";
import { withScreenContext } from "../../components/screen/ScreenContext";
import { withScrollPositionContext } from "../../components/scrollable/ScrollPositionContext";
import withScrollableView from "../../components/scrollable/withScrollableView";
import { withZoomContext } from "../../components/scrollable/ZoomContext";
import DependencyLine from "../../components/timelineView/DependencyLine";
import OutOfRangeButton from "../../components/timelineView/OutOfRangeButton";
import { ConstraintTarget } from "../../constants/constraints";
import { DragDropTypes } from "../../constants/dragDropTypes";
import { TIMELINE_VIEW } from "../../constants/identifiers";
import interfaceMessages from "../../constants/interfaceMessages";
import lineStyles from "../../constants/lineStyles";
import Orientations from "../../constants/orientations";
import PubSubTopics from "../../constants/pubSubTopics";
import timeoutRates from "../../constants/timeoutRates";
import { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import dateSubtractionFunctions from "../../modules/definition/calendar/dateFunctions/dateSubtractionFunctions";
import { stringFromTimespan } from "../../modules/definition/calendar/dateFunctions/timespanFunctions";
import { typeSelectors } from "../../modules/definition/types";
import { itemOperations } from "../../modules/items";
import { immutableRecords, PropTypeShapes } from "../../types";
import { FlatRectRecord } from "../../types/immutableRecords/helper/FlatRectRecord";
import { selectByPubSubTopics } from "../../types/immutableRecords/model/appState/utils";
import { disableWhenIos } from "../../utils/codeSplit";
import { onPasteItems } from "../../utils/copyPaste";
import { isDragMeasuringTimespan, isDragSettingSuper } from "../../utils/dropFunctions";
import { getModifierKeysForEvent } from "../../utils/getModifierKeys";
import hasAncestorOfClass from "../../utils/hasAncestorOfClass";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import { getScreenPointerPositionFromEvent, getXYPositionInDOMElement } from "../../utils/mouseUtils";
import preprocessorQuery from "../../utils/preprocessorQuery";
import rectUtils from "../../utils/rectUtils";
import { updateSelectedItemsUsingUpdaters } from "../../utils/selection";
import MindmapLine from "../mindmapView/MindmapLine";
import { withWorkspaceContext } from "../workspace/WorkspaceContext";
import layoutFunctions from "./layoutFunctions";
import selectors from "./selectors";
import TimelineGroupHeaderView from "./TimelineGroupHeaderView";
import TimelineItemChildrenBorder from "./TimelineItemChildrenBorder";
import TimelineItemNode from "./TimelineItemNode";
import timelineViewUtils from "./utils";

// import debugUtils from "../../utils/debugUtils";

const TIMELINE_DRAG_SNAP_THRESHOLD = 10;

const OUT_OF_RANGE_BUTTON_HORIZONTAL_MARGIN = 10;
const OUT_OF_RANGE_BUTTON_VERTICAL_MARGIN = 10;

const canDropOnComponent = (props, dragItem, positions, dropTarget) => {
  if (isDragMeasuringTimespan(dragItem)) {
    return false;
  }

  const isSettingSuper = isDragSettingSuper(dragItem, positions.modifierKeys);
  const canDropItems = props.requestCanDropItems(
    dragItem,
    null,
    isSettingSuper,
    dropTarget.component.state.newSuperItem
  );
  return canDropItems.canDrop;
};

const dropCallbacks = {
  onEnter(_props, _dragItem, _dropTarget) {
    // Do nothing
  },

  onLeave(props, dragItem, dropTarget) {
    dropTarget.component.onLeave(props, dragItem, dropTarget);
  },

  onHover(props, dragItem, positions, dropTarget) {
    return dropTarget.component.onHover(props, dragItem, positions, dropTarget);
  },

  onDrop(props, dragItem, positions, dropTarget) {
    const canDrop = canDropOnComponent(props, dragItem, positions, dropTarget);
    if (canDrop) {
      dropTarget.component.onDrop(props, dragItem, positions, dropTarget);
    } else {
      dropTarget.component.cancelDrop();
    }
  },

  getDragMultipleItemIds(props, dragItem, _dropTarget) {
    return props.selectedItemIds.contains(dragItem.itemId)
      ? props.sortedItemIds.filter(id => props.selectedItemIds.contains(id)).toArray()
      : [dragItem.itemId];
  },

  getDragStartParams(props, dragItem, positions, dropTarget) {
    return dropTarget.component.getDragStartParams(props, dragItem, positions, dropTarget);
  },

  getDropPreviewForItem(props, dragItem, dropTarget) {
    return dropTarget.component.getDropPreviewForItem(props, dragItem, dropTarget);
  }
};

const findClosestHorizontalAlignment = (node, absoluteX, unitScale) => {
  const startDist = node.startDate.timestamp / unitScale - absoluteX;
  const endDist = node.endDate.timestamp / unitScale - absoluteX;

  if (Math.abs(startDist) <= Math.abs(endDist)) {
    return {
      node,
      align: ConstraintTarget.Start,
      distance: startDist,
      newDate: node.startDate
    };
  } else {
    return {
      node,
      align: ConstraintTarget.End,
      distance: endDist,
      newDate: node.endDate
    };
  }
};

const findHorizontalSnap = (nodes, absoluteX, unitScale) => {
  const alignments = nodes.map(n => findClosestHorizontalAlignment(n, absoluteX, unitScale));
  const minAlign = alignments.minBy(a => Math.abs(a.distance));
  if (minAlign && Math.abs(minAlign.distance) < TIMELINE_DRAG_SNAP_THRESHOLD) {
    return minAlign;
  }
  return null;
};

const findClosestHorizontalAlignmentByPixel = (node, absoluteX) => {
  const dist = node.pixel - absoluteX;
  return {
    node,
    distance: dist,
    newDate: node.markDate
  };
};

const findHorizontalSnapByPixel = (nodes, absoluteX) => {
  const alignments = nodes.map(n => findClosestHorizontalAlignmentByPixel(n, absoluteX));
  const minAlign = alignments.minBy(a => Math.abs(a.distance));
  return minAlign;
};

const findClosestPositionOfItemToPoint = (timelineData, itemId, targetX, targetY) => {
  let position = null;
  let minDistance = null;

  timelineData.groups.forEach(group => {
    const pos = group.positions.get(itemId);
    if (pos) {
      const pX = (pos.totalRect.left + pos.totalRect.right) / 2;
      const pY = (pos.totalRect.top + pos.totalRect.bottom) / 2;
      const distance =
        Math.abs(targetX - pX) * Math.abs(targetX - pX) + Math.abs(targetY - pY) * Math.abs(targetY - pY);
      if (!minDistance || distance < minDistance) {
        position = pos;
        minDistance = distance;
      }
    }
  });
  return position;
};

const findClosestPositionOfItemToRect = (timelineData, itemId, viewport) => {
  const viewportX = (viewport.left + viewport.right) / 2;
  const viewportY = (viewport.top + viewport.bottom) / 2;
  return findClosestPositionOfItemToPoint(timelineData, itemId, viewportX, viewportY);
};

const findPositionOfItem = (timelineData, itemId) => {
  const group = timelineData.groups.find(g => g.positions.has(itemId));
  return group ? group.positions.get(itemId) : null;
};

class TimelineCanvasView extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.onDrop = this.onDrop.bind(this);
    this.cancelDrop = this.cancelDrop.bind(this);
    this.getDropPreviewForItem = this.getDropPreviewForItem.bind(this);
    this.getDragStartParams = this.getDragStartParams.bind(this);
    this.onItemClick = this.onItemClick.bind(this);
    this.onScrollToItemReceived = this.onScrollToItemReceived.bind(this);
    this.onScrollToDateReceived = this.onScrollToDateReceived.bind(this);
    this.onscrollToDualDateReceived = this.onscrollToDualDateReceived.bind(this);
    this.onSelectItemByTopicsReceived = this.onSelectItemByTopicsReceived.bind(this);
    this.onDoubleClick = this.onDoubleClick.bind(this);
    this.onCreateItem = this.onCreateItem.bind(this);
    this.onCreateMarker = this.onCreateMarker.bind(this);
    this.onCreateItemAtTimestamp = this.onCreateItemAtTimestamp.bind(this);
    this.getDragSnapToTimespan = this.getDragSnapToTimespan.bind(this);
    this.getDragSnapToMinorTick = this.getDragSnapToMinorTick.bind(this);
    this.dragArrowToMinimumZeroDuration = this.dragArrowToMinimumZeroDuration.bind(this);
    this._dependencyPositions = {};
    this._scrollToItemId = null;
    this.onDragHoverValuesChange = this.onDragHoverValuesChange.bind(this);
    this.measuringTimespanDragResult = this.measuringTimespanDragResult.bind(this);

    this.registerGroupHeaderListener = this.registerGroupHeaderListener.bind(this);
    this.unregisterGroupHeaderListener = this.unregisterGroupHeaderListener.bind(this);

    // copy paste
    this._pubSubPasteToken = null;
    this.onPasteMessageReceived = this.onPasteMessageReceived.bind(this);
    this.onPaste = this.onPaste.bind(this);
    this.processPastingItems = this.processPastingItems.bind(this);
    this.onClick = this.onClick.bind(this);

    if (!preprocessorQuery.isIos()) {
      // Mouse listeners to select events via dragging Rectangle
      this.onMouseDown = this.onMouseDown.bind(this);
      this.onMouseMove = this.onMouseMove.bind(this);
      this.throttleMouseMove = throttle(this.onMouseMove, timeoutRates.SELECT_RECTANGLE_MOUSE_MOVE_THROTTLE);
      this.onMouseUp = this.onMouseUp.bind(this);

      this.selectEventsInDraggingRect = this.selectEventsInDraggingRect.bind(this);
      this.throttledSelectEventsInDraggingRect = throttle(
        this.selectEventsInDraggingRect,
        timeoutRates.SELECT_EVENTS_IN_RECT_THROTTLE
      );
      this._selectStartPointAbs = null;
      this._lastMousePosOnScreen = null;
      this._selectRectAbs = null;
      this._lastAbsMousePosOnScreen = null;

      // Flag of is dragging or is drawing selection rectangle
      this._isMouseDown = false;
    }

    this._groupHeaderListeners = {};
    this._groupHeadersDomNode = null;
    this._groupHeadersTransformNode = null;

    this._liveScrollLeft = props.xScrollPosition.absoluteScroll;
    this._liveScrollTop = props.yScrollPosition.absoluteScroll;

    this._liveScrollListeners = {
      onLiveScrollStart: this.onLiveScrollStart.bind(this),
      onLiveScrollUpdate: this.onLiveScrollUpdate.bind(this),
      onLiveScrollStop: this.onLiveScrollEnd.bind(this)
    };

    this._outOfRangeButtonsRefs = {
      top: createRef(),
      right: createRef(),
      bottom: createRef(),
      left: createRef()
    };
    this._outOfRangeEventIds = {
      top: null,
      right: null,
      bottom: null,
      left: null
    };
    // Flag for showing out range buttons
    this._inLiveScroll = {
      horizontal: false,
      vertical: false,
      horizontalAnimated: false,
      verticalAnimated: false,
      lastActivated: null
    };

    this.state = {
      data: null
    };
  }

  componentWillMount() {
    this.updateLayout(this.props);
  }

  componentDidMount() {
    this.props.registerDropTarget(this, dropCallbacks);
    this._pubSubScrollToItemToken = PubSub.subscribe(PubSubTopics.scrollToItem, this.onScrollToItemReceived);
    this._pubSubScrollToDateToken = PubSub.subscribe(PubSubTopics.scrollToDate, this.onScrollToDateReceived);
    this._pubSubscrollToDualDateToken = PubSub.subscribe(
      PubSubTopics.scrollToDualDate,
      this.onscrollToDualDateReceived
    );
    this._pubSubSelectItemByTopicsToken = PubSub.subscribe(
      PubSubTopics.selectItemByTopics,
      this.onSelectItemByTopicsReceived
    );
    this._pubSubPasteToken = PubSub.subscribe(PubSubTopics.paste, this.onPasteMessageReceived);

    this.props.workViewRegistration.registerWorkViewCreateItemCallback(this.props.viewId, this.onCreateItem);
    this.props.workViewRegistration.registerWorkViewCreateMarkerCallback(this.props.viewId, this.onCreateMarker);

    // Register live scroll updates
    this.props.xScrollActions.registerForLiveUpdates(this._liveScrollListeners);
    this.props.yScrollActions.registerForLiveUpdates(this._liveScrollListeners);

    this.updateOutOfRangeButtons();
  }

  componentWillReceiveProps(nextProps) {
    // debugUtils.logPropChanges("TimelineCanvasView", this.props, nextProps, true);
    if (nextProps.sortedItemIds !== this.props.sortedItemIds) {
      this._lastClickItemId = null;
      this._firstShiftClickSelection = null;
    }
    this.updateLayout(nextProps);
    if (preprocessorQuery.isIos() && nextProps.shouldAutoScroll) {
      this.scrollViewportToEvents();
    }
    this._liveScrollLeft = nextProps.xScrollPosition.absoluteScroll;
    this._liveScrollTop = nextProps.yScrollPosition.absoluteScroll;
  }

  componentDidUpdate(_prevProps) {
    if (this._scrollToItemId) {
      this.onScrollToItem(this._scrollToItemId);
      this._scrollToItemId = null;
    }
    if (this._selectRectAbs) {
      this.updateSelectRectangleDom();
    }
    this.updateGroupHeaderListeners();
    this.updateOutOfRangeButtons();
  }

  componentWillUnmount() {
    this.props.workViewRegistration.unregisterWorkViewCreateItemCallback(this.props.viewId);
    this.props.workViewRegistration.unregisterWorkViewCreateMarkerCallback(this.props.viewId);
    PubSub.unsubscribe(this._pubSubScrollToItemToken);
    PubSub.unsubscribe(this._pubSubScrollToDateToken);
    PubSub.unsubscribe(this._pubSubscrollToDualDateToken);
    PubSub.unsubscribe(this._pubSubSelectItemByTopicsToken);
    PubSub.unsubscribe(this._pubSubPasteToken);

    this.props.xScrollActions.unregisterForLiveUpdates(this._liveScrollListeners);
    this.props.yScrollActions.unregisterForLiveUpdates(this._liveScrollListeners);
  }

  getEventsInVisiblePortal() {
    const { data } = this.state;
    const viewport = this.getViewport();
    const visiblePortal = selectors.getVisiblePortal(
      this.props.containerRect,
      this._liveScrollLeft,
      this._liveScrollTop
    );
    return selectors.filterEventsWithinVisiblePortal(data, viewport, visiblePortal);
  }

  updateOutOfRangeEventIds() {
    const { data } = this.state;
    const visiblePortal = selectors.getVisiblePortal(
      this.props.containerRect,
      this._liveScrollLeft,
      this._liveScrollTop
    );
    const filteredData = this.getEventsInVisiblePortal();
    if (filteredData.eventsById.size === 0) {
      this._outOfRangeEventIds = selectors.getClosestEventPerDirection(data, visiblePortal);
    } else {
      this._outOfRangeEventIds = { top: null, right: null, bottom: null, left: null };
    }
  }

  scrollViewportToEvents() {
    // In iOS mode, when switching to a new timeline view,
    // if no events in the viewport, should scroll to the earliest one
    const visibleData = this.getEventsInVisiblePortal();

    const { earliestEvent, latestEvent } = this.props;
    if (visibleData.eventsById.size === 0 && (earliestEvent || latestEvent)) {
      const centerDropTs = this.getCenterDropDate().timestamp;
      const earliestEventTs = earliestEvent.startDate.timestamp;
      const latestEventTs = latestEvent.startDate.timestamp;
      const targetEventId =
        centerDropTs < earliestEventTs ? earliestEvent.id : centerDropTs > latestEventTs ? latestEvent.id : null;
      if (targetEventId) {
        this.onScrollToItem(targetEventId);
      }
    }
  }

  canCreateItemOfType(typeId) {
    return this.props.timelineTypeIds.contains(typeId);
  }

  processPastingItems(itemMapList) {
    let extraActions = [];
    const noDateItemMapList = itemMapList.filter(im => !isDateValid(im.toJS().startDate));
    // If the pasted item has no date
    // Set the pasted item date to the selected item date
    // or the center drop date
    if (noDateItemMapList.size > 0) {
      const selectedEvent = this.props.timelineData.eventsById.find(v => this.props.selectedItemIds.includes(v.id));
      const targetDate =
        selectedEvent && isDateValid(selectedEvent.preferredStartDate)
          ? selectedEvent.preferredStartDate
          : this.getCenterDropDate();

      noDateItemMapList.forEach(im => {
        extraActions.push(
          itemOperations.setPreferredStartDate([im.get("id")], targetDate.timestamp, targetDate.precision)
        );
      });
    }

    return extraActions;
  }

  onPaste(e) {
    const {
      dispatchBatchActions,
      projectId,
      isActiveView,
      timelineTypeIds,
      defaultItemType,
      typesById,
      propertiesById,
      colorsById,
      referencesById,
      itemsById
    } = this.props;
    dispatchBatchActions(
      onPasteItems(
        e,
        projectId,
        isActiveView,
        itemMap => this.canCreateItemOfType(itemMap.get("type")),
        this.processPastingItems,
        timelineTypeIds,
        defaultItemType,
        typeSelectors.isDatedType,
        typesById,
        propertiesById,
        colorsById,
        referencesById,
        itemsById
      )
    );
  }

  onPasteMessageReceived(msg, clipboardData) {
    this.onPaste({ clipboardData });
  }

  updateLayout(useProps) {
    // Layout the events. Note this uses re-reselect, so it will only get executed
    // when something has changed, otherwise a cached value is returned.
    const data = selectors.layoutEvents(
      useProps.timelineData,
      useProps.viewId,
      useProps.ongoingDescendantsSet,
      useProps.topLevelItems,
      useProps.expandedItems,
      useProps.xScrollPosition.zoom,
      useProps.fontSize,
      useProps.calendar,
      useProps.timelineViewSettings.showItemCard,
      useProps.timelineViewSettings.eventLayout
    );

    this.setState({
      data
    });
  }

  onLeave(props, _dragItem, _dropTarget) {
    if (props.onInternalDragLeave) {
      props.onInternalDragLeave();
    }
    this.onDragHoverValuesChange(null, null, null);

    // Reset if we were dropping into a parent item or measure distance
    this.setState({ newSuperItem: null, measureToItemId: null, measureFromItemId: null, measureItemLinePoints: null });
  }

  measuringTimespanDragResult(props, dragItem, positions) {
    const targetPointerPos = positions.targetPointerPos;
    const point = {
      x: this.props.xScrollPosition.canvas.min + targetPointerPos.x,
      y: targetPointerPos.y
    };

    let measureToItemId = null;
    let measureItemLinePoints = null;
    // Find which item the pointer overlaps with
    // If the item is already nested, prefer the deepest descendant
    this.props.sortedItemIds
      .reverse()
      .filter(itemId => dragItem.multipleItemIds.indexOf(itemId) < 0)
      .forEach(itemId => {
        const position = findClosestPositionOfItemToPoint(this.state.data, itemId, point.x, point.y);
        if (!position) {
          return;
        }
        const intersection = rectUtils.pointOverlapsRect(point, position.totalRect);
        if (intersection) {
          const descendants = this.props.descendantsById.get(itemId);
          if (!descendants.has(measureToItemId)) {
            measureToItemId = itemId;
          }
        }
      });

    const measureItem = measureToItemId ? this.getRenderedEvents().eventsById.get(measureToItemId) : null;
    if (measureItem) {
      const startPos = {
        x: dragItem.initialValues.mouseDownX - props.xScrollPosition.canvas.min,
        y: dragItem.initialValues.mouseDownY
      };
      const endPos = targetPointerPos;
      measureItemLinePoints = [startPos, endPos];

      this.setState(
        {
          measureToItemId: measureItem.id,
          measureFromItemId: dragItem.initialValues.dragItem.itemId,
          measureItemLinePoints: measureItemLinePoints
        },
        () => {
          this.onDragHoverValuesChange(null, null, null);
        }
      );

      const duration = dateSubtractionFunctions.timespanBetweenDates(
        this.props.calendar,
        dragItem.initialValues.dragItemStartDate,
        measureItem.startDate
      );
      const string = stringFromTimespan(duration);
      return {
        message: `Difference between start of events: ${string}`
      };
    } else {
      this.setState(
        {
          measureToItemId: null,
          measureFromItemId: dragItem.initialValues.dragItem.itemId,
          measureItemLinePoints: measureItemLinePoints
        },
        () => {
          this.onDragHoverValuesChange(null, null, null);
        }
      );
      return {
        message: `Hover over events to measure time difference`
      };
    }
  }

  onHover(props, dragItem, positions, _dropTarget) {
    if (this._inLiveScroll.horizontal || this._inLiveScroll.vertical) {
      this.onDragHoverValuesChange(null, null, null);
      return;
    }

    const targetPointerPos = positions.targetPointerPos;
    const scrollLeft = props.xScrollPosition.canvas.min;
    const scrollRight = this.props.xScrollPosition.canvas.max;
    const unitScale = this.props.xScrollPosition.zoom;

    // What exact timestamp are we hovering over?
    // If drag position is not available, use the last mouse position - we do this when triggering a mousemove after using keyboard modifiers
    const mouseAbsX =
      targetPointerPos && targetPointerPos.x
        ? scrollLeft + targetPointerPos.x
        : this._lastAbsMousePosOnScreen
          ? this._lastAbsMousePosOnScreen.x
          : null;
    const mouseTs = Math.round(mouseAbsX * unitScale);

    // Default to dropping at mouseTs
    let dropDate = null;
    let dropDateStr = null;

    // Dragging to set super
    const isSettingSuper = isDragSettingSuper(dragItem, positions.modifierKeys);
    if (isSettingSuper) {
      let rectAbsLeft = props.xScrollPosition.canvas.min + positions.targetPointerPos.x;
      let rectAbsTop = positions.targetPointerPos.y;

      let rect;
      // We don't really care which group  we grab the position from here
      // since we are just after the size of the dragged
      // item rather than the top position (which will vary by group)
      const itemPosition = findPositionOfItem(this.state.data, dragItem.itemId);
      if (itemPosition) {
        rect = itemPosition.totalRect;
        rectAbsLeft -= dragItem.initialValues.sourceRectPointerPosX;
        rectAbsTop -= dragItem.initialValues.sourceRectPointerPosY;
      } else {
        const dropPreview = this.getDropPreviewForItem(props, dragItem);
        rect = FlatRectRecord(dropPreview.props.position.totalRect);
      }
      rect = rect
        .set("left", rectAbsLeft)
        .set("right", rect.width + rectAbsLeft)
        .set("top", rectAbsTop)
        .set("bottom", rect.height + rectAbsTop);

      let newSuperItemId = null;
      let largestArea = 0;
      // Find which item the dragged item overlaps with the most
      // If the item is already nested, prefer the deepest descendant
      props.sortedItemIds
        .reverse()
        .filter(itemId => dragItem.multipleItemIds.indexOf(itemId) < 0)
        .forEach(itemId => {
          const position = findClosestPositionOfItemToRect(this.state.data, itemId, rect);
          if (!position) {
            return;
          }
          const intersectionArea = rectUtils.rectIntersectionArea(position.totalRect, rect);
          if (intersectionArea) {
            const descendants = this.props.descendantsById.get(itemId);
            if (!descendants.has(newSuperItemId) && intersectionArea > largestArea) {
              largestArea = intersectionArea;
              newSuperItemId = itemId;
            }
          }
        });

      const newSuperItem = newSuperItemId ? this.getRenderedEvents().eventsById.get(newSuperItemId) : null;
      if (newSuperItem) {
        if (!this.state.newSuperItem || this.state.newSuperItem.id != newSuperItem.id) {
          this.setState({
            newSuperItem: newSuperItem
          });
        }
      } else if (this.state.newSuperItem) {
        this.setState({
          newSuperItem: null
        });
      }

      this.onDragHoverValuesChange(null, null, null);
      const canDropItems = props.requestCanDropItems(dragItem, dropDate, true, newSuperItem);
      const canDrop = canDropItems.canDrop;
      return {
        message: canDrop
          ? newSuperItem
            ? `Group under: ${getLabelWithUntitled(newSuperItem.label)}`
            : "Set as top level item"
          : canDropItems.message,
        messageType: canDrop ? null : "error"
      };
    } else {
      if (this.state.newSuperItem) {
        this.setState({ newSuperItem: null });
      }
    }

    // Dragging to measure timespan
    const isMeasuringTimespan = isDragMeasuringTimespan(dragItem);
    if (dragItem.sourceView === props.viewId && dragItem.initialValues.mouseDownTs) {
      if (isMeasuringTimespan) {
        return this.measuringTimespanDragResult(props, dragItem, positions);
      } else {
        if (this.state.measureToItemId) {
          this.setState({ measureToItemId: null, measureFromItemId: null, measureItemLinePoints: null });
        }
      }
    } else if (isMeasuringTimespan) {
      return {
        message: interfaceMessages.DRAG_DROP_MEASURE_ACROSS_VIEWS_NOT_ALLOWED,
        messageType: "error"
      };
    }

    // Regular drags to set dates and relationships
    if (dragItem.sourceView === props.viewId && dragItem.initialValues.mouseDownTs) {
      const isMeasuringTimespan = isDragMeasuringTimespan(dragItem);
      if (isMeasuringTimespan) {
        return this.measuringTimespanDragResult(props, dragItem, positions);
      } else {
        if (this.state.measureToItemId) {
          this.setState({ measureToItemId: null, measureFromItemId: null, measureItemLinePoints: null });
        }
      }

      // If we dragged from within same view, we actually want to apply the offset to the original date
      const deltaTs = Math.round(mouseTs - dragItem.initialValues.mouseDownTs);
      const newTs =
        (!dragItem.draggingEndArrow
          ? dragItem.initialValues.dragItemStartDate.timestamp
          : dragItem.initialValues.dragItemEndDate.timestamp) + deltaTs;
      const leftAbsX = Math.round(newTs / unitScale);
      const snapResult = !positions.modifierKeys.SHIFT
        ? this.getDragSnapToEvent(
          leftAbsX,
          dragItem.multipleItemIds,
          dragItem.draggingEndArrow ? dragItem.initialValues.dragItemStartDate : null,
          dragItem.draggingStartArrow ? dragItem.initialValues.dragItemEndDate : null
        )
        : null;

      if (snapResult) {
        dropDate = snapResult.newDate;
        dropDateStr = snapResult.dateString;

        const canDropItems = this.props.requestCanDropItems(dragItem, dropDate);
        const canDrop = canDropItems.canDrop;
        if (dragItem.draggingStartArrow || dragItem.draggingEndArrow) {
          this.setState({ dropDate: dropDate, canDrop: canDrop, dragItemId: dragItem.initialValues.dragItem.itemId });
        }

        this.onDragHoverValuesChange(canDrop ? dropDate : null, dropDateStr, positions.screenPointerPos.y);
        let sourceRect = positions.sourceRect;
        sourceRect.left += snapResult.distance;
        sourceRect.right += snapResult.distance;

        return {
          message: canDrop
            ? `Snap${dragItem.draggingStartArrow ? " start" : dragItem.draggingEndArrow ? " end" : ""} to ${
              snapResult.align === ConstraintTarget.Start ? "start" : "end"
            } of: ${getLabelWithUntitled(snapResult.node.label)}`
            : canDropItems.message,
          messageType: canDrop ? null : "error",
          sourceRect
        };
      } else {
        // If shift is pressed, snap to the timeline's current minor tick interval (not the ticks themselves, just the timespan)
        const snapToTimespan = positions.modifierKeys.SHIFT ? this.getDragSnapToTimespan() : null;
        let dragResult = this.props.getDropDateResultWithDelta(
          !dragItem.draggingEndArrow
            ? dragItem.initialValues.dragItemStartDate
            : dragItem.initialValues.dragItemEndDate,
          deltaTs,
          unitScale,
          snapToTimespan
        );
        // If dragging an end arrow too far, make it a zero duration event
        dragResult = this.dragArrowToMinimumZeroDuration(dragItem, dragResult, unitScale);
        dropDate = dragResult.newDate;
        dropDateStr = dragResult.dateString;

        const canDropItems = this.props.requestCanDropItems(dragItem, dropDate);
        const canDrop = canDropItems.canDrop;
        if (dragItem.draggingStartArrow || dragItem.draggingEndArrow) {
          this.setState({ dropDate: dropDate, canDrop: canDrop, dragItemId: dragItem.initialValues.dragItem.itemId });
        }

        this.onDragHoverValuesChange(canDrop ? dropDate : null, dropDateStr, positions.screenPointerPos.y);
        let sourceRect = positions.sourceRect;
        if (dragResult.precisionDiff) {
          sourceRect.left += dragResult.precisionDiff;
          sourceRect.right += dragResult.precisionDiff;
        }

        return {
          message: canDrop
            ? `Move${dragItem.draggingStartArrow ? " start" : dragItem.draggingEndArrow ? " end" : ""} by: ${
              dragResult.deltaString
            }`
            : canDropItems.message,
          messageType: canDrop ? null : "error",
          sourceRect
        };
      }
    } else if (dragItem.sourceViewType === TIMELINE_VIEW) {
      // If we dragged from another timeline view, then we want to add filters but not touch the date
      const canDropItems = this.props.requestCanDropItems(dragItem);
      const canDrop = canDropItems.canDrop;

      this.onDragHoverValuesChange(null, null, null);
      return {
        message: canDrop ? `Add relationships` : canDropItems.message,
        messageType: canDrop ? null : "error"
      };
    } else {
      // If we dragged from any other view type, we want to set date, and possibly add relationships also
      const leftAbsX = Math.round(mouseTs / unitScale);

      if (positions.modifierKeys.SHIFT) {
        // If shift is pressed, snap to the timeline's current minor tick interval (not the ticks themselves, just the timespan)
        const snapResult = this.getDragSnapToMinorTick(leftAbsX, scrollLeft, scrollRight);
        dropDate = snapResult.newDate;
        dropDateStr = snapResult.dateString;

        this.props.onDragHoverValuesChange(canDrop ? dropDate : null, dropDateStr, positions.screenPointerPos.y);
        let sourceRect = positions.sourceRect;
        sourceRect.left += snapResult.distance;
        sourceRect.right += snapResult.distance;
      } else {
        // Otherwise, we dragged from a different view, so we want to use the actual drop position
        const dragResult = this.props.getDropDateResult(mouseTs, unitScale);
        dropDate = dragResult.newDate;
        dropDateStr = dragResult.dateString;
      }

      const canDropItems = this.props.requestCanDropItems(dragItem, dropDate);
      const canDrop = canDropItems.canDrop;

      const message =
        props.viewFilter.relationshipsByType.size || props.viewFilter.relationshipsByReference.size
          ? "Add to Timeline and add relationships"
          : "Add to Timeline";

      this.onDragHoverValuesChange(canDrop ? dropDate : null, dropDateStr, positions.screenPointerPos.y);
      return {
        message: canDrop ? message : canDropItems.message,
        messageType: canDrop ? null : "error"
      };
    }
  }

  onDrop(props, dragItem, positions, _dropTarget) {
    const targetPointerPos = positions.targetPointerPos;
    const scrollLeft = this.props.xScrollPosition.canvas.min;
    const scrollRight = this.props.xScrollPosition.canvas.max;
    const unitScale = this.props.xScrollPosition.zoom;

    // What exact timestamp are we hovering over?
    const mouseAbsX = scrollLeft + targetPointerPos.x;
    const mouseTs = Math.round(mouseAbsX * unitScale);

    // Default to dropping at mouseTs
    let dropDate = null;
    let deltaSpan = null;

    const isMeasuringTimespan = isDragMeasuringTimespan(dragItem);
    const isSettingSuper = isDragSettingSuper(dragItem, positions.modifierKeys);

    if (isSettingSuper && !isMeasuringTimespan) {
      // If we're dragging to group
      props.onEventDropToCreateSuper(
        dragItem.sourceView,
        dragItem.sourceViewType,
        dragItem.multipleItemIds,
        this.state.newSuperItem ? this.state.newSuperItem.id : null
      );
      this.setState({
        newSuperItem: null
      });
    } else if (!isMeasuringTimespan) {
      if (dragItem.sourceView === props.viewId && dragItem.initialValues.mouseDownTs) {
        // If we dragged from within our own view, calculate the delta
        const draggedDate = !dragItem.draggingEndArrow
          ? dragItem.initialValues.dragItemStartDate
          : dragItem.initialValues.dragItemEndDate;
        const deltaTs = Math.round(mouseTs - dragItem.initialValues.mouseDownTs);
        const newTs = draggedDate.timestamp + deltaTs;
        const leftAbsX = Math.round(newTs / unitScale);
        // Only snap to events if shift is not pressed
        const snapResult = !positions.modifierKeys.SHIFT
          ? this.getDragSnapToEvent(leftAbsX, dragItem.multipleItemIds)
          : null;

        if (snapResult) {
          dropDate = snapResult.newDate;
          deltaSpan = props.requestTimespanBetweenDates(snapResult.newDate, draggedDate);
        } else {
          // If shift is pressed, snap to the timeline's current minor tick interval (not the ticks themselves, just the timespan)
          const snapToTimespan = positions.modifierKeys.SHIFT ? this.getDragSnapToTimespan() : null;
          let dragResult = this.props.getDropDateResultWithDelta(draggedDate, deltaTs, unitScale, snapToTimespan);
          // If dragging an end arrow too far, make it a zero duration event
          dragResult = this.dragArrowToMinimumZeroDuration(dragItem, dragResult, unitScale);
          dropDate = dragResult.newDate;
          deltaSpan = dragResult.deltaSpan;
        }
      } else {
        // Otherwise, we dragged from a different view
        const leftAbsX = Math.round(mouseTs / unitScale);

        if (positions.modifierKeys.SHIFT) {
          // If shift is pressed, snap to the timeline's current minor tick interval (not the ticks themselves, just the timespan)
          const snapResult = this.getDragSnapToMinorTick(leftAbsX, scrollLeft, scrollRight);
          dropDate = snapResult.newDate;
        } else {
          const dragResult = this.props.getDropDateResult(mouseTs, unitScale);
          dropDate = dragResult.newDate;
        }
      }

      props.onEventDrop(
        dragItem.sourceView,
        dragItem.sourceViewType,
        dragItem.multipleItemIds,
        deltaSpan,
        dropDate,
        dragItem.draggingStartArrow,
        dragItem.draggingEndArrow,
        dragItem.initialValues.ongoing
      );
    }

    this.onDragHoverValuesChange(null, null, null);
  }

  cancelDrop() {
    this.onDragHoverValuesChange(null, null, null);
  }

  getDragStartParams(props, dragItem, positions, _dropTarget) {
    const targetPointerPos = clone(positions.targetPointerPos);
    const screenPointerPos = clone(positions.screenPointerPos);
    const sourceRectPointerPosX = screenPointerPos.x - positions.sourceRect.left;
    const sourceRectPointerPosY = screenPointerPos.y - positions.sourceRect.top;
    const mouseDownScrollLeft = this.props.xScrollPosition.canvas.min;
    const unitScale = this.props.xScrollPosition.zoom;

    // What "pixel" did the drop occur?
    let mouseDownX = mouseDownScrollLeft + targetPointerPos.x;
    let mouseDownY = targetPointerPos.y;
    let mouseDownTs = Math.round(mouseDownX * unitScale);
    const dragEvent = this.state.data.eventsById.get(dragItem.itemId);
    const dragItemStartDate = isDateValid(dragEvent.preferredStartDate)
      ? dragEvent.preferredStartDate
      : dragEvent.startDate;
    let dragItemEndDate = isDateValid(dragEvent.preferredEndDate) ? dragEvent.preferredEndDate : dragEvent.endDate;
    // Correct the end date for ongoing events that start in the future
    if (dragEvent.ongoing && dragItemEndDate.timestamp < dragItemStartDate.timestamp) {
      dragItemEndDate = dragItemStartDate;
    }

    return {
      mouseDownScrollLeft,
      mouseDownX,
      mouseDownY,
      mouseDownTs,
      dragItemStartDate,
      dragItemEndDate,
      sourceRectPointerPosX,
      sourceRectPointerPosY,
      draggingEndArrow: dragItem.draggingEndArrow,
      draggingStartArrow: dragItem.draggingStartArrow,
      dragItem: dragItem,
      dragStartModifierKeys: positions.modifierKeys,
      ongoing: dragEvent.ongoing
    };
  }

  getDropPreviewForItem(props, dragItem, _dropTarget) {
    if (dragItem.itemType === DragDropTypes.TABLE_COLUMN || dragItem.draggingStartArrow || dragItem.draggingEndArrow) {
      return null;
    }

    const isMeasuringTimespan = isDragMeasuringTimespan(dragItem);
    if (isMeasuringTimespan) {
      return <div />;
    }

    const viewport = this.getViewport();
    const unitScale = this.props.xScrollPosition.zoom;
    const itemId =
      dragItem.itemType === DragDropTypes.NARRATIVE_COLUMN || dragItem.itemType === DragDropTypes.NARRATIVE_GROUP
        ? dragItem.multipleItemIds[0]
        : dragItem.itemId;
    const n = props.requestDragPreviewDataForItem(itemId, unitScale);
    const obj = n.itemDisplay.toObject();
    const hasChildren = false;
    const expandedChildren = props.expandedItems.contains(obj.id);

    let position = n.position.toJS();
    position.totalRect.left = 0;
    position.totalRect.top = 0;

    let totalRectValues = {
      totalRectWidth: position.totalRect.width,
      totalRectHeight: position.totalRect.height,
      totalRectLeft: position.totalRect.left,
      totalRectRight: position.totalRect.right,
      totalRectTop: position.totalRect.top,
      totalRectBottom: position.totalRect.bottom
    };

    delete position.totalRect;

    // children has been changing and causing a re-render, but it is not even used
    // by TimelineItemNode
    delete obj.children;

    return (
      <TimelineItemNode
        key={obj.id}
        {...obj}
        {...position}
        {...totalRectValues}
        itemId={obj.id}
        groupId={"DropPreview"}
        hasChildren={hasChildren}
        virtualOffsetX={0}
        virtualOffsetY={0}
        viewportWidth={viewport.width}
        viewportX={0}
        canvasRange={this.props.xScrollPosition.canvas.range}
        expandedChildren={expandedChildren}
        viewId={this.props.viewId}
        timelineViewSettings={this.props.timelineViewSettings}
        fontSize={this.props.fontSize}
        onClick={null}
        onToggleChildren={null}
        autoEditWhenBlank={false}
        isDragPreview={true}
        allowDrag={false}
        allowDragEnd={false}
        calendar={this.props.calendar}
        canDrop={false}
        showItemCard={this.props.showItemCard}
        typesById={this.props.typesById}
      />
    );
  }

  getDragSnapToEvent(absoluteX, ignoreIds, earliestDate, latestDate) {
    const visibleEvents = this.getRenderedEvents().eventsById.filter(e => ignoreIds.indexOf(e.id) < 0);
    const snapResult = findHorizontalSnap(visibleEvents, absoluteX, this.props.xScrollPosition.zoom);
    if (snapResult) {
      snapResult.dateString = this.props.requestStringForDate(snapResult.newDate);

      // Check date is valid if earliest or latest date is specified
      if (
        (isDateValid(earliestDate) && snapResult.newDate.timestamp < earliestDate.timestamp) ||
        (isDateValid(latestDate) && snapResult.newDate.timestamp > latestDate.timestamp)
      ) {
        return null;
      }
    }

    return snapResult;
  }

  getDragSnapToMinorTick(absoluteX, scrollLeft, scrollRight) {
    const minorTickDates = List(
      selectors.getTimelineMinorTickDates(
        this.props.calendar,
        this.props.viewId,
        this.props.xScrollPosition.zoom,
        scrollLeft,
        scrollRight,
        this.props.fontSize
      )
    );
    const snapResult = findHorizontalSnapByPixel(minorTickDates, absoluteX, this.props.xScrollPosition.zoom);
    if (snapResult) {
      snapResult.dateString = this.props.requestStringForDate(snapResult.newDate);
    }
    return snapResult;
  }

  getDragSnapToTimespan() {
    return selectors.getTickInterval(
      this.props.calendar,
      this.props.viewId,
      this.props.xScrollPosition.zoom,
      null,
      null,
      this.props.fontSize
    ).minorSpan;
  }

  onItemClick(itemId, modifiers = {}) {
    const processMultiSelection = (condition = false) => {
      updateSelectedItemsUsingUpdaters(
        condition,
        itemId,
        this.props.selectedItemIds,
        this.props.onRemoveFromSelection,
        this.props.onAddToSelection,
        this.props.onSelectItems
      );
    };

    //#!if iOS === false
    if (modifiers.SHIFT && this._lastClickItemId) {
      // If this is the first shift click since some other click, record the current selection
      // as the starting point for any subsequent shift clicks
      if (!this._firstShiftClickSelection) {
        this._firstShiftClickSelection = this.props.selectedItemIds;
      }
      // Select everything from last click to current position
      const a = this.props.sortedItemIds.indexOf(this._lastClickItemId);
      const b = this.props.sortedItemIds.indexOf(itemId);
      const low = Math.min(a, b);
      const high = Math.max(a, b);
      const newIds = this.props.sortedItemIds.slice(low, high + 1).toArray();
      // Note that we can't simply add to selection, as we may want to select a lesser amount than was
      // selected with the previous shift click.
      // So instead we add this new selection to the original selection before the shift clicks began
      const allIds = this._firstShiftClickSelection.push(...newIds).toArray();
      this.props.onSelectItems(allIds);
      return;
    }
    // If we have a non-shift click, record it so we can use it as the starting point for any subsequent
    // shift clicks
    this._lastClickItemId = itemId;
    // Erase the firt shift click so we start again the next time we do a shift click
    this._firstShiftClickSelection = null;

    // And then go on and handle non-shift clicks normally!
    processMultiSelection(modifiers.CONTROL || modifiers.COMMAND);
    //#!elseif iOS === true
// processMultiSelection(this.props.screenContext.isEditMode);
    //#!endif
  }

  onScrollToItemReceived(msg, data) {
    this.onScrollToItem(data.id);
  }

  scrollToFoldedItem(itemId) {
    // Check if the event is folded in a parent event,
    // try to expend to scroll to the target item
    const { data } = this.state;
    const viewport = this.getViewport();
    let itemIdsToExpand = [];

    const findToExpandSuper = id => {
      const targetItem = data.eventsById.get(id);
      if (targetItem && targetItem.super) {
        const superId = targetItem.super;
        itemIdsToExpand.push(superId);
        if (!findClosestPositionOfItemToRect(data, superId, viewport)) {
          // The super is not in position (i.e not expanded)
          // find the super of super
          findToExpandSuper(superId);
        }
      }
    };

    findToExpandSuper(itemId);
    if (itemIdsToExpand.length > 0) {
      this.props.onEventToggleChildren(itemIdsToExpand, true);
      this.onScrollToItem(itemId, true);
    }
  }

  onScrollToItem(itemId, doNotScrollAgain = false) {
    const { data } = this.state;
    const viewport = this.getViewport();
    const position = findClosestPositionOfItemToRect(data, itemId, viewport);
    if (position) {
      const r = position.totalRect;
      this.props.xScrollActions.animateRectIntoView(r);
      this.props.yScrollActions.animateRectIntoView(r);
    } else if (!doNotScrollAgain) {
      this.scrollToFoldedItem(itemId);
    }
  }

  onScrollToDateReceived(_msg, date) {
    if (date) {
      this.props.xScrollActions.animateToViewport(date.timestamp / this.props.xScrollPosition.zoom);
    }
  }

  onscrollToDualDateReceived(_msg, data) {
    const startDate = data.startDate;
    const endDate = data.endDate;
    if (isDateValid(startDate) && isDateValid(endDate)) {
      const newLensUnits = endDate.timestamp - startDate.timestamp;
      let newUnitScale = newLensUnits / this.props.containerRect.width;
      if (newUnitScale < this.props.zoomMinimum) {
        newUnitScale = this.props.zoomMinimum;
      } else if (newUnitScale > this.props.zoomMaximum) {
        newUnitScale = this.props.zoomMaximum;
      }
      this.props.xScrollActions.jumpToViewport(
        startDate.timestamp / newUnitScale,
        this.props.yScrollPosition.absoluteScroll,
        newUnitScale
      );
    } else if (startDate) {
      this.props.xScrollActions.animateToViewport(startDate.timestamp / this.props.xScrollPosition.zoom);
    }
  }

  onSelectItemByTopicsReceived(msg, selectTopic) {
    const sortedEventIds = this.state.data.sortedEventIds;
    const eventsById = this.state.data.eventsById;
    const sortedEvents = sortedEventIds.map(id => eventsById.get(id));
    const newSelectedIds = selectByPubSubTopics(
      this.props.isActiveView,
      sortedEvents,
      selectTopic,
      this.props.selectedItemIds
    );
    if (newSelectedIds.length > 0) {
      this.props.onSelectItems(newSelectedIds);
      this.onScrollToItemReceived("", { id: newSelectedIds[0] });
    }
  }

  getViewport() {
    return selectors.getViewportFromScrollPositions(this.props.xScrollPosition, this.props.yScrollPosition);
  }

  getVirtualOffset() {
    return immutableRecords.PointRecord({
      x: this.props.xScrollPosition.canvas.min,
      y: this.props.yScrollPosition.canvas.min
    });
  }

  getRenderedEvents() {
    const data = this.state.data;
    const viewport = this.getViewport();
    return selectors.filterEventsWithinViewport(data, viewport);
  }

  renderEvents(visibleData, selectedItemNodes, virtualOffset, viewport, layoutConstants, isActiveView) {
    let allowDrag = true;
    //#!if iOS === true
// allowDrag = this.props.screenContext.isEditMode;
    //#!endif

    const children = [];
    this._dependencyPositions = {};
    visibleData.groups.forEach(g => {
      const groupEventSet = g.eventIds.toSet();
      g.positions.forEach((position, id) => {
        const e = visibleData.eventsById.get(id);
        const obj = e.toObject();
        const hasChildren = e.children.filter(cId => groupEventSet.has(cId)).size > 0;
        const expandedChildren = this.props.expandedItems.contains(obj.id);
        const selected = selectedItemNodes.indexOf(e.id) >= 0;
        const allowDragEnd = selectedItemNodes.size <= 1 && !obj.isDateLocked;

        const positionValues = omit(position.toObject(), ["totalRect"]);

        let totalRectValues = {
          totalRectWidth: position.totalRect.width,
          totalRectHeight: position.totalRect.height,
          totalRectLeft: position.totalRect.left,
          totalRectRight: position.totalRect.right,
          totalRectTop: position.totalRect.top,
          totalRectBottom: position.totalRect.bottom
        };

        // children has been changing and causing a re-render, but it is not even used
        // by TimelineItemNode
        delete obj.children;

        children.push(
          <TimelineItemNode
            key={`${g.id}-${obj.id}`}
            {...obj}
            {...positionValues}
            {...totalRectValues}
            hasChildren={hasChildren}
            itemId={obj.id}
            groupId={g.id}
            isActiveView={isActiveView}
            selected={selected}
            allowDrag={allowDrag}
            expandedChildren={expandedChildren}
            viewId={this.props.viewId}
            timelineViewSettings={this.props.timelineViewSettings}
            virtualOffsetX={virtualOffset.x}
            virtualOffsetY={virtualOffset.y}
            viewportWidth={viewport.width}
            viewportX={viewport.left}
            canvasRange={this.props.xScrollPosition.canvas.range}
            fontSize={this.props.fontSize}
            onClick={this.onItemClick}
            onToggleChildren={this.props.onEventToggleChildren}
            onLabelChange={this.props.onLabelChange}
            autoEditWhenBlank={this.props.autoEditBlankNodes}
            isDragPreview={false}
            allowDragEnd={allowDragEnd}
            calendar={this.props.calendar}
            isMeasuring={
              this.state.measureToItemId === obj.id || this.state.measureFromItemId === obj.id ? true : false
            }
            dropDate={this.state.dragItemId === obj.id ? this.state.dropDate : null}
            unitScale={this.state.dragItemId === obj.id ? this.props.xScrollPosition.zoom : null}
            canDrop={this.state.dragItemId === obj.id ? this.state.canDrop : false}
            showItemCard={this.props.showItemCard}
            hasnewSuperItem={this.state.newSuperItem && this.state.newSuperItem.id === obj.id}
            onShowInspector={this.props.onShowInspector}
            typesById={this.props.typesById}
          />
        );

        if (expandedChildren) {
          const borderRect = position.childrenBorderRect.toObject();
          if (borderRect.width > 0) {
            borderRect.top -= virtualOffset.y;
            borderRect.left -= virtualOffset.x;

            let left = borderRect.left;
            let width = borderRect.width;
            let leftMin = -10000;

            if (left < leftMin) {
              width = width + left - leftMin;
              left = leftMin;
              borderRect.width = width;
              borderRect.left = left;
            }
            if (width > this.props.xScrollPosition.canvas.range) {
              width = this.props.xScrollPosition.canvas.range;
              borderRect.width = width;
            }

            let gapLeft = 0;
            let gapWidth = 0;
            if (this.props.timelineViewSettings.showItemCard) {
              gapLeft = Math.min(
                position.markerRelativeRect.left,
                position.uncertainStartRect.width ? position.uncertainStartRect.left : position.markerRelativeRect.left,
                position.startRectWithChildren.width
                  ? position.startRectWithChildren.left
                  : position.markerRelativeRect.left
              );
              gapWidth =
                position.childrenArrowRect.left +
                position.childrenArrowRect.width -
                gapLeft +
                layoutConstants.EVENT_RECT_PADDING * 2 -
                (position.childrenBorderRect.width - width);
            }

            children.push(
              <TimelineItemChildrenBorder
                key={`${g.id}-${obj.id}-childrenBorder`}
                rect={borderRect}
                color={obj.color}
                opacity={obj.isFiltered ? 0.4 : 1}
                gapLeft={gapLeft}
                gapWidth={gapWidth}
              />
            );
          }
        }
      });
    });
    return children;
  }

  registerGroupHeaderListener(id, listeners) {
    this._groupHeaderListeners[id] = listeners;
  }

  unregisterGroupHeaderListener(id) {
    delete this._groupHeaderListeners[id];
  }

  updateGroupHeaderListeners() {
    const virtualOffset = this.getVirtualOffset();
    const visibleData = this.getRenderedEvents();
    // iterate through each of our listeners and tell it where
    // the header should be displayed.

    if (this._inLiveScroll.lastActivated === Orientations.HORIZONTAL && this._inLiveScroll.horizontal) {
      // When scrolling horizontally, we have to switch to a fixed
      // position. Which means we need to calculate the correct y
      // position for the item on the screen rather than within the
      // scroll view.
      visibleData.groups.reverse().forEach(group => {
        const groupTop = group.bounds.top;
        // const groupBottom = groupTop + group.headerHeight;
        const listeners = this._groupHeaderListeners[group.id];
        const left = this.props.containerRect.left;
        const top = this.props.containerRect.top + groupTop - this._liveScrollTop;
        if (listeners) {
          listeners.switchToFixedPosition(left, top);
        }
      });
    } else {
      // When scrolling vertically (or not scrolling), we switch
      // to an absolute position so that the item scrolls naturally within
      // the screen.
      // Therefore, the top goes to its usual place in the scroll view, but
      // the left value needs to be placed based on the current scroll position.
      visibleData.groups.reverse().forEach(group => {
        const listeners = this._groupHeaderListeners[group.id];
        const groupTop = group.bounds.top;
        const left = this._liveScrollLeft - virtualOffset.x;
        const top = groupTop;
        if (listeners) {
          listeners.switchToAbsolutePosition(left, top);
        }
      });
    }
  }

  renderGroupHeaders(visibleData, _virtualOffset, _viewport) {
    const children = [];
    visibleData.groups.forEach(g => {
      if (g.showHeader) {
        children.push(
          <TimelineGroupHeaderView
            key={g.id}
            id={g.id}
            label={g.label}
            icon={g.icon}
            iconColor={g.iconColor}
            top={g.bounds.top}
            height={g.headerHeight}
            registerScrollListener={this.registerGroupHeaderListener}
            unregisterScrollListener={this.unregisterGroupHeaderListener}
          />
        );
      }
    });
    return children;
  }

  renderDependencyLines(data, virtualOffset, validatedDependencies, validatedConstraints, layoutConstants) {
    const depLines = selectors.calculateOffsetDependencyLinePositions(
      this.props.viewId,
      data,
      validatedDependencies,
      validatedConstraints,
      layoutConstants,
      virtualOffset
    );

    const lineComponents = depLines.map(dp => {
      return (
        <DependencyLine
          key={dp.id}
          subjectPoint={dp.subjectPoint}
          isSubjectLeft={dp.subjectStart}
          objectPoint={dp.objectPoint}
          isObjectLeft={dp.objectStart}
          isViolated={dp.isViolated}
        />
      );
    });
    return lineComponents;
  }

  renderMeasureItemLine() {
    return (
      <MindmapLine
        id={`connectorDragLine`}
        selected={true}
        points={this.state.measureItemLinePoints}
        color="iconActive"
        lineStyle={lineStyles.SOLID}
      />
    );
  }

  onDoubleClick(e) {
    // Ignore any double clicks that occur on the item nodes themselves
    if (hasAncestorOfClass(e.target, "timelineItemNode")) {
      return;
    }
    // And otherwise add a new event where the double click occurred
    const unitScale = this.props.xScrollPosition.zoom;
    let newTs = timelineViewUtils.getMouseTimespan(e, this, this.props.xScrollPosition.canvas.min, unitScale);

    const modifierKeys = getModifierKeysForEvent(e);
    if (modifierKeys.SHIFT) {
      // If shift is pressed, snap to the timeline's minor ticks
      const leftAbsX = Math.round(newTs / unitScale);
      const scrollLeft = this.props.xScrollPosition.canvas.min;
      const scrollRight = this.props.xScrollPosition.canvas.max;
      const snapResult = this.getDragSnapToMinorTick(leftAbsX, scrollLeft, scrollRight);
      newTs = snapResult.newDate.timestamp;
    }

    this.onCreateItemAtTimestamp(null, newTs);
  }

  getCenterTimestamp() {
    // TODO: Consider placing at end of selected event instead if there is
    // a current selection.
    const unitScale = this.props.xScrollPosition.zoom;
    const viewport = this.props.xScrollPosition.viewport;
    const absX = (viewport.min + viewport.max) / 2;
    const ts = Math.round(absX * unitScale);
    return ts;
  }

  getDropDateOfTimestamp(ts) {
    const unitScale = this.props.xScrollPosition.zoom;
    // TODO: make this pass in calendar
    const dragResult = this.props.getDropDateResult(ts, unitScale);
    const dropDate = dragResult.newDate;
    return dropDate;
  }

  getCenterDropDate() {
    return this.getDropDateOfTimestamp(this.getCenterTimestamp());
  }

  onCreateItem(typeId) {
    if (!this.canCreateItemOfType(typeId)) {
      return false;
    }
    const ts = this.getCenterTimestamp();
    this.onCreateItemAtTimestamp(typeId, ts);
    return true;
  }

  onCreateMarker() {
    const unitScale = this.props.xScrollPosition.zoom;
    const viewport = this.props.xScrollPosition.viewport;
    const absX = (viewport.min + viewport.max) / 2;
    const ts = Math.round(absX * unitScale);
    const dragResult = this.props.getDropDateResult(ts, unitScale);
    const date = dragResult.newDate;
    this.props.onCreateNewMarkerWithDate(date);
    return true;
  }

  onCreateItemAtTimestamp(typeId, ts) {
    const dropDate = this.getDropDateOfTimestamp(ts);
    this.props.onCreateNewEventWithDate(dropDate, typeId);
  }

  // Drag selection rectangle functions
  //#!if iOS !== true
  screenToAbsolutePoint(x, y) {
    const p = getXYPositionInDOMElement(x, y, this._domNode);
    return {
      x: p.x + this.props.xScrollPosition.canvas.min,
      y: p.y + this.props.yScrollPosition.canvas.min
    };
  }

  relativeToAbsolutePoint(x, y) {
    return {
      x: x + this.props.xScrollPosition.canvas.min,
      y: y + this.props.yScrollPosition.canvas.min
    };
  }

  absoluteToRelativePoint(point) {
    return {
      x: point.x - this.props.xScrollPosition.canvas.min,
      y: point.y - this.props.yScrollPosition.canvas.min
    };
  }

  registerDragListeners() {
    let isTouch = false;

    // Add event listeners
    document.addEventListener("mousemove", this.throttleMouseMove);
    document.addEventListener("mouseup", this.onMouseUp);

    // And signal to the scrollViews that a drag is in progress, so that
    // they auto-scroll when required
    PubSub.publish(PubSubTopics.dragStarted, { isTouch });
  }

  unregisterDragListeners() {
    // Remove event listeners
    document.removeEventListener("mousemove", this.throttleMouseMove);
    document.removeEventListener("mouseup", this.onMouseUp);

    // And signal to the scrollViews that the drag has ended
    PubSub.publish(PubSubTopics.dragEnded);
  }

  onMouseDown(e) {
    this._isMouseDown = true;

    // Should only response to mouse left button click
    if (!e || (e.type === "mousedown" && e.button !== 0)) {
      return;
    }
    // Get the last mouse position for keyboard modifier keys or selection rectangle
    const p = getScreenPointerPositionFromEvent(e);
    this._lastMousePosOnScreen = p;
    this._lastAbsMousePosOnScreen = this.screenToAbsolutePoint(p.x, p.y);
    // For dragging the selection rectangle
    if (!hasAncestorOfClass(e.target, "timelineItemNode")) {
      this._stopClickEvent = false;
      this._liveScrollLeft = this.props.xScrollPosition.absoluteScroll;
      this._liveScrollTop = this.props.yScrollPosition.absoluteScroll;
      this._selectStartPointAbs = this.screenToAbsolutePoint(p.x, p.y);
    }
    // Register last because we get a liveScroll event as soon as we register
    this.registerDragListeners();
  }

  onMouseMove(e) {
    const p = getScreenPointerPositionFromEvent(e);
    this._lastMousePosOnScreen = p;
    this._lastAbsMousePosOnScreen = this.screenToAbsolutePoint(p.x, p.y);
    this.updateDragRectAndSelection();
  }

  onMouseUp(e) {
    this._isMouseDown = false;

    // Stop listening
    this.unregisterDragListeners();

    // If mouse position has moved, should stop the click event
    // the parent click event may unselect all items
    if (this._selectRectAbs && (this._selectRectAbs.width > 5 || this._selectRectAbs.height > 5)) {
      this._stopClickEvent = true;
    }

    // Remove the old selections
    this._selectRectAbs = null;
    this._selectStartPointAbs = null;

    this.updateSelectRectangleDom();

    // If we were dropping into a parent item, remove the last target
    if (this.state.newSuperItem) {
      const modifierKeys = getModifierKeysForEvent(e);
      if (!modifierKeys.CONTROL && !modifierKeys.COMMAND) {
        this.setState({ newSuperItem: null });
      }
    }
    if (this.state.measureFromItemId) {
      this.setState({ measureToItemId: null, measureFromItemId: null, measureItemLinePoints: null });
    }
  }

  updateDragRectAndSelection() {
    if (this._selectStartPointAbs) {
      const { x, y } = this._lastMousePosOnScreen;
      const endPoint = this.screenToAbsolutePoint(x, y);
      this._selectRectAbs = rectUtils.rectFromPoints(this._selectStartPointAbs, endPoint);

      // Update end point and the select rectangle
      this.updateSelectRectangleDom();
      this.throttledSelectEventsInDraggingRect();
    }
  }

  selectEventsInDraggingRect() {
    // Calls to this function are throttled at 80ms because it is a slower calculation
    if (this._selectRectAbs) {
      let selectedIdsObj = {};
      this.state.data.groups.forEach(group => {
        if (rectUtils.rectOverlapsRect(group.bounds, this._selectRectAbs)) {
          group.positions.forEach((p, id) => {
            if (rectUtils.rectOverlapsRect(p.totalRect, this._selectRectAbs)) {
              selectedIdsObj[id] = true;
            }
          });
        }
      });
      let selectedIds = Object.keys(selectedIdsObj);
      if (!isEqual(selectedIds, this._lastSelectedIds)) {
        this._lastSelectedIds = selectedIds;
        this.props.onSelectItems(selectedIds);
      }
    }
  }

  updateSelectRectangleDom() {
    if (this._selectRectangleDom) {
      let left = 0,
        top = 0,
        width = 0,
        height = 0;

      if (this._selectRectAbs) {
        left = this._selectRectAbs.left - this.props.xScrollPosition.canvas.min;
        top = this._selectRectAbs.top - this.props.yScrollPosition.canvas.min;
        width = this._selectRectAbs.width;
        height = this._selectRectAbs.height;
      }

      this._selectRectangleDom.style.width = `${width}px`;
      this._selectRectangleDom.style.left = `${left}px`;
      this._selectRectangleDom.style.height = `${height}px`;
      this._selectRectangleDom.style.top = `${top}px`;
    }
  }
  //#!endif

  updateOutOfRangeButtons() {
    // If we are within an animated scroll, then we don't want to be updating
    // the buttons on the fly
    // const hideAll = false;
    // const hideAll = this._inLiveScroll.horizontal || this._inLiveScroll.vertical;
    const hideAll = this._inLiveScroll.horizontalAnimated || this._inLiveScroll.verticalAnimated;

    // First, update the event IDs
    this.updateOutOfRangeEventIds();

    // Now update the visibility of each button
    each(this._outOfRangeButtonsRefs, (ref, direction) => {
      const dom = ref ? ref.current : null;
      if (dom) {
        const isVisible = Boolean(this._outOfRangeEventIds[direction]) && !hideAll;
        dom.style.visibility = isVisible ? "visible" : "hidden";
      }
    });
  }

  onLiveScrollStart(isHorizontal, isVertical, isAnimated) {
    if (isHorizontal) {
      this._inLiveScroll.horizontal = true;
      this._inLiveScroll.horizontalAnimated = isAnimated;
      this._inLiveScroll.lastActivated = Orientations.HORIZONTAL;
    }
    if (isVertical) {
      this._inLiveScroll.vertical = true;
      this._inLiveScroll.verticalAnimated = isAnimated;
      this._inLiveScroll.lastActivated = Orientations.VERTICAL;
    }
    this.updateGroupHeaderListeners();
    this.updateOutOfRangeButtons();
  }

  onLiveScrollUpdate(scrollLeft, scrollTop, _isAnimated) {
    if (!isNil(scrollLeft) && this._liveScrollLeft !== scrollLeft) {
      this._liveScrollLeft = scrollLeft;

      // If we receive a stray update after the scrolling has switched orientations
      // then we need to update our group header positions to follow
      if (this._inLiveScroll.lastActivated !== Orientations.HORIZONTAL) {
        this.updateGroupHeaderListeners();
      }
    }
    if (!isNil(scrollTop) && this._liveScrollTop !== scrollTop) {
      this._liveScrollTop = scrollTop;

      // If we receive a stray update after the scrolling has switched orientations
      // then we need to update our group header positions to follow
      if (this._inLiveScroll.lastActivated !== Orientations.VERTICAL) {
        this.updateGroupHeaderListeners();
      }
    }
    if (!preprocessorQuery.isIos() && this._isMouseDown) {
      this.updateDragRectAndSelection();
    }
    this.updateOutOfRangeButtons();
  }

  onLiveScrollEnd(isHorizontal, isVertical) {
    if (isHorizontal) {
      this._inLiveScroll.horizontal = false;
      this._inLiveScroll.horizontalAnimated = false;
    }
    if (isVertical) {
      this._inLiveScroll.vertical = false;
      this._inLiveScroll.verticalAnimated = false;
    }
    this.updateGroupHeaderListeners();
    this.updateOutOfRangeButtons();
  }

  onClick(e) {
    if (this._stopClickEvent) {
      e.stopPropagation();
      this._stopClickEvent = false;
    }
  }

  onDragHoverValuesChange(dropDate, dropDateStr, screenPointerPosY) {
    // Save the last dropdate so if you switch between dropping into parent and dropping at date
    // by pressing ctrl we can go back to showing the correct preview
    if (dropDate) {
      this._lastDropDate = dropDate;
      this._lastDropDateStr = dropDateStr;
      this._lastScreenPointerPosY = screenPointerPosY;
    }
    this.props.onDragHoverValuesChange(dropDate, dropDateStr, screenPointerPosY);
  }

  dragArrowToMinimumZeroDuration(dragItem, dragResult, unitScale) {
    let newResult = dragResult;
    if (dragItem.draggingEndArrow && newResult.newDate.timestamp < dragItem.initialValues.dragItemStartDate.timestamp) {
      newResult = this.props.getDropDateResultWithDelta(
        dragItem.initialValues.dragItemEndDate,
        dragItem.initialValues.dragItemStartDate.timestamp - dragItem.initialValues.dragItemEndDate.timestamp,
        unitScale
      );
    } else if (
      dragItem.draggingStartArrow &&
      newResult.newDate.timestamp > dragItem.initialValues.dragItemEndDate.timestamp
    ) {
      newResult = this.props.getDropDateResultWithDelta(
        dragItem.initialValues.dragItemStartDate,
        dragItem.initialValues.dragItemEndDate.timestamp - dragItem.initialValues.dragItemStartDate.timestamp,
        unitScale
      );
    }
    return newResult;
  }

  onOutOfRangeButtonClick(direction) {
    const eventId = this._outOfRangeEventIds[direction];
    if (eventId) {
      this.onScrollToItem(eventId);
    }
  }

  getOutOfRangeButtonStyles() {
    const containerRect = this.props.containerRect;
    const middleX = (containerRect.left + containerRect.right) / 2;
    const middleY = (containerRect.top + containerRect.bottom) / 2;
    const paddingLeft = this.props.paddingLeft ? this.props.paddingLeft : 0;
    const paddingRight = this.props.paddingRight ? this.props.paddingRight : 0;

    const BUTTON_WIDTH = 110;
    const BUTTON_HEIGHT = 30;

    const result = {
      top: {
        left: middleX - BUTTON_WIDTH / 2,
        top: containerRect.top + OUT_OF_RANGE_BUTTON_VERTICAL_MARGIN
      },
      right: {
        left: containerRect.right - BUTTON_WIDTH - OUT_OF_RANGE_BUTTON_HORIZONTAL_MARGIN - paddingRight,
        top: middleY - BUTTON_HEIGHT / 2
      },
      bottom: {
        left: middleX - BUTTON_WIDTH / 2,
        top: containerRect.bottom - BUTTON_HEIGHT - OUT_OF_RANGE_BUTTON_VERTICAL_MARGIN
      },
      left: {
        left: containerRect.left + OUT_OF_RANGE_BUTTON_HORIZONTAL_MARGIN + paddingLeft,
        top: middleY - BUTTON_HEIGHT / 2
      }
    };
    return result;
  }

  renderOutOfRangeButton(label, direction, style) {
    return (
      <OutOfRangeButton
        key={direction}
        direction={direction}
        label={label}
        onClick={() => this.onOutOfRangeButtonClick(direction)}
        style={style}
        ref={this._outOfRangeButtonsRefs[direction]}
      />
    );
  }

  renderOutOfRangeButtons() {
    const styles = this.getOutOfRangeButtonStyles();
    return [
      this.renderOutOfRangeButton("Items above", "top", styles.top),
      this.renderOutOfRangeButton("Items below", "bottom", styles.bottom),
      this.state.data.groups.size === 1 ? this.renderOutOfRangeButton("Next Item", "right", styles.right) : null,
      this.state.data.groups.size === 1 ? this.renderOutOfRangeButton("Previous Item", "left", styles.left) : null
    ];
  }

  render() {
    const viewport = this.getViewport();
    const virtualOffset = this.getVirtualOffset();

    const data = this.state.data;

    const visibleData = this.getRenderedEvents();
    const showItemCard = this.props.timelineViewSettings.showItemCard;
    const layoutConstants = layoutFunctions.getLayoutConstants(this.props.fontSize, showItemCard);

    let result = (
      <div
        className="timelineViewGlobalCanvas"
        onPaste={this.onPaste}
        onDoubleClick={disableWhenIos(this.onDoubleClick)}
        onMouseDown={this.onMouseDown}
        onClick={this.onClick}
        ref={node => (this._domNode = node)}
      >
        <div
          className={`timelineViewEventCanvas ${showItemCard ? "showItemCard" : "hideItemCard"}`}
          ref={node => (this._canvasNode = node)}
        >
          {this.renderGroupHeaders(visibleData, virtualOffset, viewport)}
          {this.props.timelineViewSettings.showDependencies
            ? this.renderDependencyLines(
              data,
              virtualOffset,
              this.props.validatedDependencies,
              this.props.validatedConstraints,
              layoutConstants
            )
            : null}
          {this.renderEvents(
            visibleData,
            this.props.selectedItemIds,
            virtualOffset,
            viewport,
            layoutConstants,
            this.props.isActiveView
          )}
          {this.state.measureItemLinePoints ? this.renderMeasureItemLine() : null}
          {/* #!if iOS !== true */}
          <div className="selectRectangle" ref={node => (this._selectRectangleDom = node)} />
          {/* #!endif */}
          {this.renderOutOfRangeButtons()}
        </div>
      </div>
    );
    return result;
  }
}

TimelineCanvasView.propTypes = {
  fontSize: PropTypes.number.isRequired,
  timelineData: PropTypes.instanceOf(immutableRecords.TimelineDisplayRecord).isRequired,
  ongoingDescendantsSet: ImmutablePropTypes.setOf(PropTypes.string).isRequired,
  descendantsById: ImmutablePropTypes.map.isRequired,
  earliestEvent: PropTypeShapes.item,
  latestEvent: PropTypeShapes.item,
  shouldAutoScroll: PropTypes.bool,
  sortedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  topLevelItems: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  expandedItems: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  // timelineViewSettings: PropTypeShapes.timelineViewSettingsJs.isRequired,
  timelineViewSettings: PropTypes.instanceOf(immutableRecords.TimelineViewSettingsRecord).isRequired,
  viewId: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  isActiveView: PropTypes.bool.isRequired,
  onSelectItems: PropTypes.func.isRequired,
  onAddToSelection: PropTypes.func.isRequired,
  onRemoveFromSelection: PropTypes.func.isRequired,
  onEventToggleChildren: PropTypes.func.isRequired,
  onEventDrop: PropTypes.func.isRequired,
  onEventDropToCreateSuper: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func,
  onCreateNewEventWithDate: PropTypes.func.isRequired,
  onCreateNewMarkerWithDate: PropTypes.func.isRequired,
  validatedDependencies: PropTypeShapes.validatedDependencyMap.isRequired,
  validatedConstraints: ImmutablePropTypes.map.isRequired,
  timelineTypeIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  defaultItemType: PropTypes.string,

  // copy paste
  dispatchBatchActions: PropTypes.func,
  typesById: ImmutablePropTypes.map,
  propertiesById: ImmutablePropTypes.map,
  colorsById: ImmutablePropTypes.map,
  referencesById: ImmutablePropTypes.map,

  requestDragPreviewDataForItem: PropTypes.func.isRequired,
  requestCanDropItems: PropTypes.func.isRequired,
  getDropDateResultWithDelta: PropTypes.func.isRequired,
  getDropDateResult: PropTypes.func.isRequired,
  requestStringForDate: PropTypes.func.isRequired,
  requestTimespanBetweenDates: PropTypes.func.isRequired,
  onDragHoverValuesChange: PropTypes.func.isRequired,
  autoEditBlankNodes: PropTypes.bool.isRequired,
  itemsById: ImmutablePropTypes.map.isRequired,
  calendar: PropTypeShapes.calendar.isRequired,
  viewFilterLabel: PropTypes.string,
  showItemCard: PropTypes.bool.isRequired,

  onShowInspector: PropTypes.func,

  // From asDropTarget
  registerDropTarget: PropTypes.func.isRequired,

  // Also accessed by ScrollableView
  requestViewBoundsForZoom: PropTypes.func.isRequired,

  // Passed in from WorkspaceContext
  workViewRegistration: PropTypes.object.isRequired,

  // From withScrollPositionContext
  xScrollPosition: PropTypes.instanceOf(immutableRecords.ScrollPositionRecord).isRequired,
  xScrollActions: PropTypeShapes.scrollActions.isRequired,
  yScrollPosition: PropTypes.instanceOf(immutableRecords.ScrollPositionRecord).isRequired,
  yScrollActions: PropTypeShapes.scrollActions.isRequired,

  // From withContainerContext
  containerRect: PropTypeShapes.flatRect.isRequired,
  paddingLeft: PropTypes.number,
  paddingRight: PropTypes.number,

  // From withZoomContext
  zoom: PropTypes.number,
  zoomMinimum: PropTypes.number,
  zoomMaximum: PropTypes.number,

  //#!if iOS === true
  // From withScreenContext
// screenContext: PropTypes.object
  //#!endif
};

const scrollableViewOptions = {
  scrollHorizontally: false,
  scrollVertically: true,
  showHorizontalScrollbar: false,
  showVerticalScrollbar: true,
  disableCtrlScroll: true
};

const normalView = withWorkspaceContext(
  withScrollableView(
    withScrollPositionContext(asDropTarget(withZoomContext(withContainerContext(TimelineCanvasView), TIMELINE_VIEW))),
    scrollableViewOptions
  )
);

let exportedView = normalView;
//#!if iOS === true
// exportedView = withScreenContext(normalView);
//#!endif

export default exportedView;
